import CancelIcon from "@mui/icons-material/Clear";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  InputAdornment,
  Paper,
  Typography,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import { useState } from "react";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import { EMPLOYEE_ID, EMPLOYEE_DATA_LOOKUP_TITLE } from "../../util/constants";
import { validateEmployee, validateEmployeeId } from "../../util/inputValidation";
import { fetchEmployee } from "../../api/employee";
import { fetchLatestUserAccessOverrideEvent } from "../../api/userAccess";
import { fetchSuperuserAccessByEmployee } from "../../api/userAccess";
import { getLastUpdatedTimeString } from "../../util/timeZoneService";
import { MULTI_STATION_ADMIN_ACCESS, ADMIN_ACCESS, EMPLOYEE_ACCESS } from "../../util/constants";

const useStyles = makeStyles((theme) => ({
  gridContentStyle: {
    marginTop: "10px",
  },
  dialogTitle: {
    fontWeight: "bold",
    marginLeft: "20px",
  },
  buttonStyle: {
    padding: "15px",
  },
  actionButton: {
    marginRight: "35px",
    marginBottom: "10px",
    width: "100px",
  },
  inputText: {
    width: "247px",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  employeeIdInputRow: {
    display: "flex",
    paddingTop: "16px",
  },

  employeeInfoLoading: {
    marginLeft: "20px",
  },
}));

const EmployeeDataLookupDialog = (props) => {
  const classes = useStyles();

  // Employee Data
  const [employeeId, setEmployeeId] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeDataErrors, setEmployeeDataErrors] = useState(null);
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");
  const [employeeIdSearchLoaded, setEmployeeIdSearchLoaded] = useState(false);
  const [employeeIdSearchTriggered, setEmployeeIdSearchTriggered] = useState(false);

  // Access Exceptions
  const [accessExceptionTempStation, setAccessExceptionTempStation] = useState(null);
  const [accessExceptionHomeStation, setAccessExceptionHomeStation] = useState(null);

  // Superuser Access
  const [superuserAccessRows, setSuperuserAccessRows] = useState(null);

  const CancelButtonEmployeeId = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleEmployeeDataClear}>
        <CancelIcon />
      </IconButton>
    );
  };

  const handleEmployeeDataClear = () => {
    setEmployeeId("");
    setEmployeeData(null);
    setEmployeeDataErrors(null);
    let errors = validateEmployeeId("");
    if (errors !== "") {
      setEmployeeIdErrors(errors);
    }
  };

  const handleEmployeeIdChange = (eid) => {
    if (employeeData) {
      setEmployeeData(null);
      setEmployeeDataErrors(null);
    }
    setEmployeeId(eid.target.value.toLowerCase());
  };

  const handleEmployeeIdBlur = (event) => {
    let trimEmpId = event?.target?.value?.trim();
    let errors = validateEmployeeId(trimEmpId);
    if (employeeIdErrors === "" || errors !== "") {
      setEmployeeIdErrors(errors);
    }
    if (errors === "") {
      if (!employeeData || trimEmpId !== employeeData.eid) {
        fetchEmployeeContent(trimEmpId);
      }
    }
  };

  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };

  const handleView = (eid) => {
    if (!employeeData || eid !== employeeData.eid) {
      let errors = validateEmployeeId(eid);
      if (errors === "") fetchEmployeeContent(eid);
      if (employeeIdErrors === "" || errors !== "") {
        setEmployeeIdErrors(errors);
      }
    }
  };

  const handleOkButtonClicked = () => {
    props.setIsDialogOpen(false);
  };

  const fetchEmployeeData = async (eid) => {
    try {
      const response = await fetchEmployee(eid).then((res) => res.json());

      if (!response.error && !response.message) {
        let employeeDataErrors = validateEmployee(
          response.eid,
          response.firstName,
          response.lastName,
          response.homeStation,
          response.workgroup
        );

        if (employeeDataErrors !== "") {
          setEmployeeIdErrors(employeeDataErrors);
          throw new Error("Invalid Employee ID");
        }
        setEmployeeData(response);
        return response;
      } else {
        setEmployeeIdErrors(employeeDataErrors);
        throw new Error("Invalid Employee ID");
      }
    } catch (error) {
      throw error;
    }
  };

  const callAccessExceptionsApi = async (eid, station, isTempStation) => {
    try {
      const response = await fetchLatestUserAccessOverrideEvent(eid, station).then((res) => res.json());

      if (!response.error && !response.message) {
        isTempStation
          ? setAccessExceptionTempStation(response.userAccessOverrideEvent)
          : setAccessExceptionHomeStation(response.userAccessOverrideEvent);
      } else {
        setEmployeeIdErrors(response.message || "Invalid Employee ID");
        throw new Error("Invalid Employee ID");
      }
    } catch (error) {
      throw error;
    }
  };

  const fetchAccessExceptions = async (fetchEmployeeDataResult) => {
    try {
      if (fetchEmployeeDataResult.eventType !== "DELETE") {
        await callAccessExceptionsApi(fetchEmployeeDataResult.eid, fetchEmployeeDataResult.homeStation, false);

        // tempStation if on temp
        if (fetchEmployeeDataResult.tempStation) {
          await callAccessExceptionsApi(fetchEmployeeDataResult.eid, fetchEmployeeDataResult.tempStation, true);
        }
      }
    } catch (error) {
      console.error("Error during fetching Access Exceptions: ", error);
    }
  };

  const fetchSuperuserStatus = async (eid) => {
    try {
      const response = await fetchSuperuserAccessByEmployee(eid).then((res) => res.json());
      if (!response.error && !response.message) {
        setSuperuserAccessRows(response.superuserAccessRows);
      } else {
        setEmployeeIdErrors(employeeDataErrors);
        throw new Error("Invalid Employee ID");
      }
    } catch (error) {
      console.error("Error during fetching Superuser Status: ", error);
    }
  };

  const fetchEmployeeContent = async (eid) => {
    try {
      setEmployeeIdSearchLoaded(false);
      setEmployeeIdSearchTriggered(true);
      setEmployeeIdErrors("");
      setEmployeeData(null);
      setEmployeeDataErrors(null);

      // Call APIs
      const fetchEmployeeDataResult = await fetchEmployeeData(eid);
      await fetchAccessExceptions(fetchEmployeeDataResult);
      await fetchSuperuserStatus(eid);

      // render the content
      setEmployeeIdSearchLoaded(true);
    } catch (error) {
      console.error("Error during fetching Employee data: ", error);
      setEmployeeIdErrors(error.message);
      setEmployeeIdSearchLoaded(true);
    }
  };

  const renderDialogTitle = () => {
    return (
      <DialogTitle className={classes.dialogTitle} id="alert-dialog-slide-title">
        {EMPLOYEE_DATA_LOOKUP_TITLE}
        {renderEmployeeIdField()}
      </DialogTitle>
    );
  };

  const renderEmployeeIdField = () => {
    return (
      <div className={classes.employeeIdInputRow}>
        <div className={classes.employeeIdInput}>
          <TextField
            data-testid="employeeDataLookup-eid-textField"
            className={classes.inputText}
            label={EMPLOYEE_ID}
            variant="outlined"
            value={employeeId}
            onChange={handleEmployeeIdChange}
            onBlur={handleEmployeeIdBlur}
            onKeyUp={handleEmployeeIdEnterKeyPressed}
            InputProps={{
              endAdornment:
                employeeId !== "" ? (
                  <InputAdornment position="start">
                    <CancelButtonEmployeeId />
                  </InputAdornment>
                ) : (
                  ""
                ),
            }}
            error={employeeIdErrors !== ""}
            helperText={employeeIdErrors}
          />
        </div>
        <div>
          <Button
            data-testid="employeeDataLookup-view-button"
            className={classes.buttonStyle}
            variant="contained"
            color="primary"
            onClick={() => handleView(employeeId)}
          >
            View
          </Button>
        </div>
      </div>
    );
  };

  const renderBasicInformation = () => {
    return (
      <Paper className="detail-dialog-section">
        <Typography component={"div"}>
          <Box className="detail-dialog-section-title">Basic Information</Box>
        </Typography>
        <Grid container className={classes.gridContentStyle}>
          <Grid item xs={6}>
            <div>EID</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.eid}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Person Number [Kronos]</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.personNumber}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Name [Kronos]</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {employeeData.lastName}, {employeeData.firstName}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Short Name [Kronos]</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.shortName}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Home Station [Kronos]</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.homeAccountStation}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Home Station</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.homeStation}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Temp Station</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.tempStation ? employeeData.tempStation : "-"}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Active Station</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.activeStation}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>isActive [Kronos]</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.isActive ? "Yes" : "No"}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>FT/PT</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.ftpt}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Job Code [Kronos]</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.homeAccountJobCode}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Job Title</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.jobTitle}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Workgroup</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.workgroup}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Phone Number</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.phoneNumber ? employeeData.phoneNumber : "-"}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Union Contract</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.unionContract}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Modified By</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {employeeData.createdBy === "CLIENT" ? (
                <>
                  {employeeData.modifiedByLastName}, {employeeData.modifiedByFirstName} ({employeeData.modifiedByEid})
                </>
              ) : (
                employeeData.createdBy
              )}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Last Updated</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {getLastUpdatedTimeString(employeeData.createdAt, "America/Chicago")}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderDates = () => {
    return (
      <Paper className="detail-dialog-section">
        <Typography component={"div"}>
          <Box className="detail-dialog-section-title">Dates</Box>
        </Typography>
        <Grid container className={classes.gridContentStyle}>
          <Grid item xs={6}>
            <div>Classification Date</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {employeeData.classificationDate ? employeeData.classificationDate : "-"}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Contract Date</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {employeeData.contractDate ? employeeData.contractDate : "-"}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>WN SLI Seniority Date</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {employeeData.wnSliSeniorityDate ? employeeData.wnSliSeniorityDate : "-"}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>FL ATL Seniority Date</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {employeeData.flAtlSeniorityDate ? employeeData.flAtlSeniorityDate : "-"}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Dual Seniority Date</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {employeeData.dualSeniorityDate ? employeeData.dualSeniorityDate : "-"}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Seniority Date</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {employeeData.seniorityDate ? employeeData.seniorityDate : "-"}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Hire Date</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.hireDate ? employeeData.hireDate : "-"}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Birth Date</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">{employeeData.birthDate ? employeeData.birthDate : "-"}</div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Modified By</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {employeeData.createdBy === "CLIENT" ? (
                <>
                  {employeeData.modifiedByLastName}, {employeeData.modifiedByFirstName} ({employeeData.modifiedByEid})
                </>
              ) : (
                employeeData.createdBy
              )}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <div>Last Updated</div>
          </Grid>
          <Grid item xs={6}>
            <div className="detail-dialog-right-text">
              {getLastUpdatedTimeString(employeeData.createdAt, "America/Chicago")}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderSuperuserStatusContent = () => {
    return superuserAccessRows.map((item) => (
      <Grid container className={classes.gridContentStyle}>
        <Grid item xs={6}>
          <div>
            <strong>{item.station}</strong>
          </div>
        </Grid>
        <Grid item xs={12}></Grid>
        <Grid item xs={6}>
          <div>Job Code</div>
        </Grid>
        <Grid item xs={6}>
          <div className="detail-dialog-right-text">{item.jobCode}</div>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={6}>
          <div>Job Title</div>
        </Grid>
        <Grid item xs={6}>
          <div className="detail-dialog-right-text">{item.jobTitle}</div>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={6}>
          <div>Created At</div>
        </Grid>
        <Grid item xs={6}>
          <div className="detail-dialog-right-text">{getLastUpdatedTimeString(item.createdAt, "America/Chicago")}</div>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
    ));
  };

  const renderSuperuserStatus = () => {
    return (
      <Paper className="detail-dialog-section">
        <Typography component={"div"}>
          <Box className="detail-dialog-section-title">Superuser Status</Box>
        </Typography>
        {superuserAccessRows.length > 0 ? renderSuperuserStatusContent() : <Grid container>Not applicable</Grid>}
      </Paper>
    );
  };

  const getAccessLevelLabel = (accessExceptionEvent) => {
    if (accessExceptionEvent.isEmployee) {
      return EMPLOYEE_ACCESS;
    }
    if (accessExceptionEvent.isAdmin) {
      return ADMIN_ACCESS;
    }
    if (accessExceptionEvent.isMultiStationAdmin) {
      return MULTI_STATION_ADMIN_ACCESS;
    }
    return "";
  };

  const renderAccessExceptionContent = (isHomeStation) => {
    const accessExceptionEvent = isHomeStation ? accessExceptionHomeStation : accessExceptionTempStation;

    return (
      <Grid container className={classes.gridContentStyle}>
        <Grid item xs={6}>
          <div>
            <strong>
              {isHomeStation ? "Home Station" : "Temp Station"}: {accessExceptionEvent.station}
            </strong>
          </div>
        </Grid>
        <Grid item xs={12}></Grid>
        <Grid item xs={6}>
          <div>Access Level</div>
        </Grid>
        <Grid item xs={6}>
          <div className="detail-dialog-right-text">{getAccessLevelLabel(accessExceptionEvent)}</div>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={6}>
          <div>Modified By</div>
        </Grid>
        <Grid item xs={6}>
          <div className="detail-dialog-right-text">
            {accessExceptionEvent.modifiedByLastName}, {accessExceptionEvent.modifiedByFirstName} (
            {accessExceptionEvent.modifiedByEid})
          </div>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={6}>
          <div>Modified At</div>
        </Grid>
        <Grid item xs={6}>
          <div className="detail-dialog-right-text">
            {getLastUpdatedTimeString(accessExceptionEvent.createdAt, "America/Chicago")}
          </div>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {accessExceptionEvent.isMultiStationAdmin && (
          <>
            <Grid item xs={6}>
              <div>
                <div>{MULTI_STATION_ADMIN_ACCESS}</div>
                <div>Station List</div>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className="detail-dialog-right-text">
                {accessExceptionEvent.multiStationAdminStationList.join(", ")}
              </div>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  const renderAccessExceptions = () => {
    return (
      <Paper className="detail-dialog-section">
        <Typography component={"div"}>
          <Box className="detail-dialog-section-title">Access Exceptions</Box>
        </Typography>
        {accessExceptionHomeStation !== null ? (
          renderAccessExceptionContent(true)
        ) : (
          <Grid container>Not applicable</Grid>
        )}
        {accessExceptionTempStation !== null && employeeData.tempStation && renderAccessExceptionContent(false)}
      </Paper>
    );
  };

  const renderEmployeeDataContent = () => {
    return (
      <DialogContentText component={"div"} id="alert-dialog-slide-description">
        <Grid container className={classes.gridContentStyle}>
          <Grid item xs={6}>
            {renderBasicInformation()}
            {renderAccessExceptions()}
          </Grid>
          <Grid item xs={6}>
            {renderDates()}
            {renderSuperuserStatus()}
          </Grid>
        </Grid>
      </DialogContentText>
    );
  };

  const renderDialogContent = () => {
    return (
      <DialogContent style={{ height: "570px" }}>
        {employeeIdSearchTriggered &&
          (employeeIdSearchLoaded ? (
            employeeIdErrors === "" && employeeData && renderEmployeeDataContent()
          ) : (
            <CircularProgress className={classes.employeeInfoLoading} />
          ))}
      </DialogContent>
    );
  };

  const renderDialogActions = () => {
    return (
      <DialogActions>
        <div>
          <Button className={classes.actionButton} variant="contained" color="primary" onClick={handleOkButtonClicked}>
            Close
          </Button>
        </div>
      </DialogActions>
    );
  };

  return (
    <div>
      <Dialog
        className="employee-data-lookup-dialog"
        data-testid="employee-data-lookup-dialog"
        fullWidth
        fullScreen={!props.isDesktopView}
        maxWidth="md"
        open={props.isDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
        onClose={handleOkButtonClicked}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        {renderDialogTitle()}
        {renderDialogContent()}
        {renderDialogActions()}
      </Dialog>
    </div>
  );
};

EmployeeDataLookupDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  setIsDialogOpen: PropTypes.func.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default EmployeeDataLookupDialog;
